// Preview, parallel or live, and write-back to the current (old) hub.
// Parallel: copied data can be edited here while the copy from the current hub carries on (edits made here are kept
// here and not sent to the current hub), so for the pages it counts as live.
// Until switch day the current hub is the master copy, so edits to copied data are refused by the server —
// unless write-back is switched on and the action is one the new hub can write through to the current hub
// (GET /api/system → writeBack.capabilities). canWrite(capability) is true when the hub is live, or when that
// capability is written back. <WriteBackHint> says so next to those actions.
import { useQuery } from '@tanstack/react-query'
import { api } from './api'

export interface SystemInfo {
  mode: 'preview' | 'parallel' | 'live'
  banner: string
  version?: string
  writeBack?: { enabled: boolean; capabilities: string[] }
}

export function useSystem() {
  return useQuery({ queryKey: ['system'], queryFn: () => api.get<SystemInfo>('/api/system'), staleTime: 60_000 })
}

export function useMode() {
  const { data } = useSystem()
  const live = data?.mode === 'live' || data?.mode === 'parallel'
  const caps = new Set(data?.writeBack?.capabilities ?? [])
  return {
    /** Copied data can be edited here (live or parallel). */
    live,
    preview: !live,
    parallel: data?.mode === 'parallel',
    loaded: !!data,
    /** True when this change can be made here now: live, or written back to the current hub in preview. */
    canWrite: (capability: string) => live || caps.has(capability),
    /** True when this change is made in preview by writing to the current hub first. */
    writesBack: (capability: string) => !live && caps.has(capability),
    writeBackEnabled: !!data?.writeBack?.enabled,
  }
}

/** Small note next to actions that, in preview, are saved to the current hub first. Renders nothing otherwise. */
export function WriteBackHint({ capability, style }: { capability: string | string[]; style?: React.CSSProperties }) {
  const mode = useMode()
  const caps = Array.isArray(capability) ? capability : [capability]
  if (!caps.some((c) => mode.writesBack(c))) return null
  return (
    <span className="muted" style={{ fontSize: 12, ...style }} title="Preview: this change is saved in the current hub (still the master copy) first, then shown here.">
      Writes to the current hub
    </span>
  )
}
